<template>
  <div class="app-content pt-3 p-md-3 p-lg-4">
    <div class="container-fluid">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="app-page-title mb-0">Course Registration Management</h1>
        <button class="btn btn-primary" @click="openCreateModal">
          <b-icon icon="plus-circle"></b-icon> Create Registration
        </button>
      </div>

      <div class="app-card shadow-sm mb-4">
        <div class="app-card-body p-4">
          <!-- Filters -->
          <div class="row mb-3">
            <div class="col-md-3">
              <label class="small text-muted mb-1">Session / Semester</label>
              <v-select
                v-model="selectedSetting"
                :options="settings"
                :get-option-label="s => s.session_name + ' / ' + s.semester_name"
                placeholder="All Sessions/Semesters"
                @input="onSettingChange"
                :reduce="s => s"
              ></v-select>
            </div>
            <div class="col-md-3">
              <label class="small text-muted mb-1">Department</label>
              <v-select
                v-model="selectedDepartment"
                :options="departments"
                label="department"
                placeholder="All Departments"
                @input="onFilterChange"
                :reduce="d => d"
              ></v-select>
            </div>
            <div class="col-md-3">
              <label class="small text-muted mb-1">Programme</label>
              <v-select
                v-model="selectedProgramme"
                :options="filteredProgrammes"
                label="programme"
                placeholder="All Programmes"
                @input="onFilterChange"
                :reduce="p => p"
              ></v-select>
            </div>
            <div class="col-md-2">
              <label class="small text-muted mb-1">Status</label>
              <v-select
                v-model="selectedStatus"
                :options="statusOptions"
                label="text"
                placeholder="All Status"
                @input="onFilterChange"
                :reduce="s => s"
              ></v-select>
            </div>
            <div class="col-md-1 d-flex align-items-end pb-1">
              <button class="btn btn-info btn-block" @click="loadRegistrations" :disabled="loading">
                <b-spinner small v-if="loading"></b-spinner>
                <b-icon icon="search" v-else></b-icon>
              </button>
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-md-4">
              <b-form-input
                v-model="searchText"
                placeholder="Search student name, email, matric..."
                @keyup.enter="loadRegistrations"
                size="sm"
              ></b-form-input>
            </div>
            <div class="col-md-2">
              <small class="text-muted" v-if="registrations.length">{{ registrations.length }} result(s)</small>
            </div>
          </div>

          <b-table
            :items="registrations"
            :fields="fields"
            :busy="loading"
            :per-page="perPage"
            :current-page="currentPage"
            responsive
            striped
            hover
            show-empty
          >
            <template #table-busy>
              <div class="text-center my-4">
                <b-spinner variant="primary"></b-spinner>
                <div class="mt-2 text-muted">Loading registrations...</div>
              </div>
            </template>
            <template #empty>
              <div class="text-center text-muted py-4">No registrations found.</div>
            </template>
            <template #cell(index)="data">
              {{ (currentPage - 1) * perPage + data.index + 1 }}
            </template>
            <template #cell(student_name)="data">
              {{ data.item.surname }} {{ data.item.first_name }}
            </template>
            <template #cell(session_semester)="data">
              {{ data.item.session_name }} / {{ data.item.semester_name }}
            </template>
            <template #cell(reg_status)="data">
              <b-badge :variant="data.value == '1' ? 'success' : 'warning'">
                {{ data.value == '1' ? 'Approved' : 'Pending' }}
              </b-badge>
            </template>
            <template #cell(actions)="data">
              <button class="btn btn-sm btn-outline-info mr-1" @click="viewCourses(data.item)" title="Manage courses">
                <b-icon icon="pencil-square"></b-icon>
              </button>
              <button
                v-if="data.item.reg_status != '1'"
                class="btn btn-sm btn-success mr-1"
                @click="approveReg(data.item)"
                :disabled="actionId === data.item.id"
                title="Approve"
              >
                <b-spinner small v-if="actionId === data.item.id && actionType === 'approve'"></b-spinner>
                <b-icon icon="check-circle" v-else></b-icon>
              </button>
              <button
                v-if="data.item.reg_status == '1'"
                class="btn btn-sm btn-warning mr-1"
                @click="disapproveReg(data.item)"
                :disabled="actionId === data.item.id"
                title="Disapprove"
              >
                <b-spinner small v-if="actionId === data.item.id && actionType === 'disapprove'"></b-spinner>
                <b-icon icon="x-circle" v-else></b-icon>
              </button>
            </template>
          </b-table>

          <div class="d-flex justify-content-between align-items-center" v-if="registrations.length > perPage">
            <small class="text-muted">Page {{ currentPage }} of {{ Math.ceil(registrations.length / perPage) }}</small>
            <b-pagination
              v-model="currentPage"
              :total-rows="registrations.length"
              :per-page="perPage"
              size="sm"
            ></b-pagination>
          </div>
        </div>
      </div>

      <!-- Manage Courses Modal -->
      <b-modal
        v-model="showCoursesModal"
        :title="coursesModalTitle"
        size="xl"
        ok-only
        ok-title="Close"
        scrollable
      >
        <div v-if="loadingCourses" class="text-center my-3">
          <b-spinner variant="primary"></b-spinner>
        </div>
        <template v-else>
          <div class="mb-3">
            <small class="text-muted">
              Session: <strong>{{ selectedRegInfo.session_name }}</strong> |
              Semester: <strong>{{ selectedRegInfo.semester_name }}</strong> |
              Level: <strong>Year {{ selectedRegInfo.level }}</strong> |
              Status: <b-badge :variant="selectedRegInfo.reg_status == '1' ? 'success' : 'warning'">{{ selectedRegInfo.reg_status == '1' ? 'Approved' : 'Pending' }}</b-badge>
            </small>
          </div>

          <!-- Registered Courses Table -->
          <h6>Registered Courses ({{ selectedCourses.length }})</h6>
          <b-table
            v-if="selectedCourses.length"
            :items="selectedCourses"
            :fields="courseFields"
            responsive
            striped
            small
          >
            <template #cell(course_status)="data">
              <b-badge :variant="data.value === 'C' ? 'primary' : 'warning'">
                {{ data.value === 'C' ? 'Compulsory' : 'Elective' }}
              </b-badge>
            </template>
            <template #cell(actions)="data">
              <button
                class="btn btn-sm btn-outline-danger"
                @click="removeCourse(data.item)"
                :disabled="removingId === data.item.id"
                title="Remove course"
              >
                <b-spinner small v-if="removingId === data.item.id"></b-spinner>
                <b-icon icon="trash" v-else></b-icon>
              </button>
            </template>
          </b-table>
          <div v-else class="text-center text-muted py-3 border rounded mb-3">No courses registered.</div>
          <div class="text-right mb-3">
            <strong>Total Units: {{ totalUnits }}</strong>
          </div>

          <hr>

          <!-- Add Course Section -->
          <h6>Add Course</h6>
          <div class="row mb-2">
            <div class="col-md-5">
              <b-form-input
                v-model="courseSearch"
                placeholder="Search course code or title..."
                @keyup.enter="searchCourses"
                size="sm"
              ></b-form-input>
            </div>
            <div class="col-md-2">
              <button class="btn btn-sm btn-info btn-block" @click="searchCourses" :disabled="searchingCourses || !courseSearch.trim()">
                <b-spinner small v-if="searchingCourses"></b-spinner>
                <span v-else>Search</span>
              </button>
            </div>
          </div>

          <b-table
            v-if="availableCourses.length"
            :items="availableCourses"
            :fields="availableCourseFields"
            responsive
            striped
            small
            :per-page="10"
            :current-page="courseResultPage"
          >
            <template #cell(add)="data">
              <div class="d-flex align-items-center">
                <b-form-select v-model="data.item._status" size="sm" style="width:100px" class="mr-1">
                  <option value="C">Compulsory</option>
                  <option value="E">Elective</option>
                </b-form-select>
                <button
                  class="btn btn-sm btn-success"
                  @click="addCourse(data.item)"
                  :disabled="addingCourseId === data.item.id"
                  title="Add course"
                >
                  <b-spinner small v-if="addingCourseId === data.item.id"></b-spinner>
                  <b-icon icon="plus-circle" v-else></b-icon>
                </button>
              </div>
            </template>
          </b-table>
          <div class="d-flex justify-content-end" v-if="availableCourses.length > 10">
            <b-pagination
              v-model="courseResultPage"
              :total-rows="availableCourses.length"
              :per-page="10"
              size="sm"
            ></b-pagination>
          </div>
        </template>
      </b-modal>

      <!-- Create Registration Modal -->
      <b-modal
        v-model="showCreateModal"
        title="Create Course Registration"
        size="lg"
        @ok="submitCreateRegistration"
        ok-title="Create Registration"
        :ok-disabled="creatingReg || !newReg.student || !newReg.setting || !newReg.programme || !newReg.level"
        scrollable
      >
        <div class="mb-3">
          <label class="font-weight-bold">Search Student</label>
          <div class="row">
            <div class="col-md-8">
              <b-form-input
                v-model="studentSearch"
                placeholder="Search by name, email, or matric number..."
                @keyup.enter="searchStudentsForReg"
                size="sm"
              ></b-form-input>
            </div>
            <div class="col-md-4">
              <button class="btn btn-sm btn-info btn-block" @click="searchStudentsForReg" :disabled="searchingStudents || !studentSearch.trim()">
                <b-spinner small v-if="searchingStudents"></b-spinner>
                <span v-else>Search</span>
              </button>
            </div>
          </div>
          <div v-if="studentResults.length" class="mt-2 border rounded" style="max-height:200px;overflow-y:auto">
            <div
              v-for="s in studentResults"
              :key="s.id"
              class="p-2 border-bottom d-flex justify-content-between align-items-center"
              :class="{ 'bg-light': newReg.student && newReg.student.id === s.id }"
              style="cursor:pointer"
              @click="selectStudent(s)"
            >
              <div>
                <strong>{{ s.surname }} {{ s.first_name }}</strong>
                <small class="text-muted ml-2">{{ s.matric_collect || s.matric_no || '' }}</small>
              </div>
              <small class="text-muted">{{ s.email }}</small>
            </div>
          </div>
          <div v-if="newReg.student" class="mt-2 alert alert-info py-2 mb-0">
            Selected: <strong>{{ newReg.student.surname }} {{ newReg.student.first_name }}</strong>
            ({{ newReg.student.matric_collect || newReg.student.matric_no || newReg.student.email }})
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <label class="font-weight-bold">Session / Semester</label>
            <v-select
              v-model="newReg.setting"
              :options="settings"
              :get-option-label="s => s.session_name + ' / ' + s.semester_name"
              placeholder="Select session/semester"
            ></v-select>
          </div>
          <div class="col-md-6">
            <label class="font-weight-bold">Programme</label>
            <v-select
              v-model="newReg.programme"
              :options="programmes"
              :get-option-label="p => p.code + ' - ' + p.programme"
              :filter="filterProgrammes"
              placeholder="Type to search programme..."
            ></v-select>
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-md-4">
            <label class="font-weight-bold">Level (Year)</label>
            <b-form-input v-model.number="newReg.level" type="number" min="1" max="10" placeholder="e.g. 1"></b-form-input>
          </div>
        </div>
        <div v-if="creatingReg" class="text-center mt-3">
          <b-spinner small variant="primary"></b-spinner> Creating registration...
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CourseRegistration',
  data() {
    return {
      registrations: [],
      settings: [],
      departments: [],
      programmes: [],
      loading: false,
      selectedSetting: null,
      selectedDepartment: null,
      selectedProgramme: null,
      selectedStatus: null,
      searchText: '',
      statusOptions: [
        { value: '1', text: 'Approved' },
        { value: '0', text: 'Pending' },
      ],
      currentPage: 1,
      perPage: 20,
      fields: [
        { key: 'index', label: '#' },
        { key: 'student_name', label: 'Student' },
        { key: 'matric_no', label: 'Matric No' },
        { key: 'email', label: 'Email' },
        { key: 'department_name', label: 'Department' },
        { key: 'session_semester', label: 'Session/Semester' },
        { key: 'level', label: 'Level' },
        { key: 'reg_status', label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      actionId: null,
      actionType: null,
      showCoursesModal: false,
      coursesModalTitle: '',
      selectedCourses: [],
      selectedRegInfo: {},
      selectedRegId: null,
      loadingCourses: false,
      removingId: null,
      courseSearch: '',
      searchingCourses: false,
      availableCourses: [],
      addingCourseId: null,
      courseResultPage: 1,
      showCreateModal: false,
      creatingReg: false,
      studentSearch: '',
      searchingStudents: false,
      studentResults: [],
      newReg: {
        student: null,
        setting: null,
        programme: null,
        level: 1,
      },
      courseFields: [
        { key: 'code', label: 'Code' },
        { key: 'description', label: 'Description' },
        { key: 'course_unit', label: 'Unit', class: 'text-center' },
        { key: 'course_status', label: 'Type', class: 'text-center' },
        { key: 'actions', label: '', class: 'text-center', thStyle: { width: '60px' } },
      ],
      availableCourseFields: [
        { key: 'code', label: 'Code' },
        { key: 'description', label: 'Description' },
        { key: 'unit', label: 'Unit', class: 'text-center' },
        { key: 'add', label: 'Add', class: 'text-center', thStyle: { width: '180px' } },
      ],
    };
  },
  computed: {
    filteredProgrammes() {
      if (this.selectedDepartment) {
        var deptId = this.selectedDepartment.id;
        return this.programmes.filter(function(p) { return p.department_id == deptId; });
      }
      return this.programmes;
    },
    totalUnits() {
      return this.selectedCourses.reduce(function(sum, c) { return sum + (parseInt(c.course_unit) || 0); }, 0);
    },
  },
  created() {
    this.loadSettings();
    this.loadDepartments();
    this.loadProgrammes();
  },
  methods: {
    filterProgrammes(options, search) {
      var s = search.toLowerCase();
      return options.filter(function(p) {
        return (p.programme || '').toLowerCase().indexOf(s) > -1
          || (p.code || '').toLowerCase().indexOf(s) > -1
          || (p.degree || '').toLowerCase().indexOf(s) > -1;
      });
    },
    openCreateModal() {
      this.showCreateModal = true;
      this.studentSearch = '';
      this.studentResults = [];
      this.newReg = {
        student: null,
        setting: this.selectedSetting || null,
        programme: null,
        level: 1,
      };
    },
    searchStudentsForReg() {
      if (!this.studentSearch.trim()) return;
      this.searchingStudents = true;
      axios.get('/admin/course-registrations/search-students', { params: { q: this.studentSearch.trim() } })
        .then(res => { this.studentResults = res.data.students || []; })
        .catch(() => { Toast.fire({ icon: 'error', title: 'Student search failed' }); })
        .finally(() => { this.searchingStudents = false; });
    },
    selectStudent(student) {
      this.newReg.student = student;
    },
    submitCreateRegistration(evt) {
      evt.preventDefault();
      if (!this.newReg.student || !this.newReg.setting || !this.newReg.programme || !this.newReg.level) return;
      this.creatingReg = true;
      axios.post('/admin/course-registrations/create', {
        student_id: this.newReg.student.id,
        settings_id: this.newReg.setting.id,
        programme_id: this.newReg.programme.id,
        level: this.newReg.level,
      })
        .then(res => {
          Toast.fire({ icon: 'success', title: res.data.message || 'Registration created' });
          this.showCreateModal = false;
          this.loadRegistrations();
          var regId = res.data.registration_id;
          if (regId) {
            this.selectedRegId = regId;
            this.coursesModalTitle = this.newReg.student.surname + ' ' + this.newReg.student.first_name + ' - Manage Courses';
            this.showCoursesModal = true;
            this.availableCourses = [];
            this.courseSearch = '';
            this.courseResultPage = 1;
            this.loadRegCourses();
          }
        })
        .catch(err => {
          var msg = err.response && err.response.data && err.response.data.message;
          Toast.fire({ icon: 'error', title: msg || 'Failed to create registration' });
        })
        .finally(() => { this.creatingReg = false; });
    },
    loadSettings() {
      axios.get('/admin/all-settings').then(res => {
        this.settings = res.data.data || [];
        var active = this.settings.find(function(s) { return s.status === 'active'; });
        if (active) {
          this.selectedSetting = active;
        } else if (this.settings.length) {
          this.selectedSetting = this.settings[0];
        }
        this.loadRegistrations();
      });
    },
    loadDepartments() {
      axios.get('/admin/departments').then(res => {
        this.departments = res.data.data || [];
      });
    },
    loadProgrammes() {
      axios.get('/admin/programmes').then(res => {
        this.programmes = res.data.data || [];
      });
    },
    onSettingChange() {
      this.loadRegistrations();
    },
    onFilterChange() {
      if (this.selectedDepartment && this.selectedProgramme) {
        if (this.selectedProgramme.department_id != this.selectedDepartment.id) {
          this.selectedProgramme = null;
        }
      }
      this.loadRegistrations();
    },
    loadRegistrations() {
      this.loading = true;
      this.currentPage = 1;
      var params = {};
      if (this.selectedSetting) params.settings_id = this.selectedSetting.id;
      if (this.selectedDepartment) params.department_id = this.selectedDepartment.id;
      if (this.selectedProgramme) params.programme_id = this.selectedProgramme.id;
      if (this.selectedStatus) params.reg_status = this.selectedStatus.value;
      if (this.searchText.trim()) params.search = this.searchText.trim();

      axios.get('/admin/course-registrations', { params: params })
        .then(res => { this.registrations = res.data.registrations || []; })
        .catch(() => { Toast.fire({ icon: 'error', title: 'Failed to load registrations' }); })
        .finally(() => { this.loading = false; });
    },
    viewCourses(item) {
      this.coursesModalTitle = item.surname + ' ' + item.first_name + ' (' + (item.matric_no || '') + ') - Manage Courses';
      this.showCoursesModal = true;
      this.selectedRegId = item.id;
      this.availableCourses = [];
      this.courseSearch = '';
      this.courseResultPage = 1;
      this.loadRegCourses();
    },
    loadRegCourses() {
      this.loadingCourses = true;
      this.selectedCourses = [];
      this.selectedRegInfo = {};
      axios.get('/admin/course-registrations/' + this.selectedRegId + '/courses')
        .then(res => {
          this.selectedCourses = res.data.courses || [];
          this.selectedRegInfo = res.data.registration || {};
        })
        .catch(() => { Toast.fire({ icon: 'error', title: 'Failed to load courses' }); })
        .finally(() => { this.loadingCourses = false; });
    },
    searchCourses() {
      if (!this.courseSearch.trim()) return;
      this.searchingCourses = true;
      this.courseResultPage = 1;
      axios.get('/admin/courses', { params: { search: this.courseSearch.trim() } })
        .then(res => {
          var registeredIds = this.selectedCourses.map(function(c) { return c.course_id; });
          this.availableCourses = (res.data.courses || [])
            .filter(function(c) { return registeredIds.indexOf(c.id) === -1; })
            .map(function(c) { c._status = 'C'; return c; });
        })
        .catch(() => { Toast.fire({ icon: 'error', title: 'Failed to search courses' }); })
        .finally(() => { this.searchingCourses = false; });
    },
    addCourse(course) {
      this.addingCourseId = course.id;
      axios.post('/admin/course-registrations/' + this.selectedRegId + '/add-course', {
        course_id: course.id,
        course_unit: course.unit,
        course_status: course._status,
      })
        .then(() => {
          Toast.fire({ icon: 'success', title: course.code + ' added' });
          this.loadRegCourses();
          this.availableCourses = this.availableCourses.filter(function(c) { return c.id !== course.id; });
        })
        .catch(err => {
          var msg = err.response && err.response.data && err.response.data.message;
          Toast.fire({ icon: 'error', title: msg || 'Failed to add course' });
        })
        .finally(() => { this.addingCourseId = null; });
    },
    removeCourse(regCourse) {
      Swal.fire({
        title: 'Remove ' + regCourse.code + '?',
        text: 'This will remove the course from this registration.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, remove',
      }).then(result => {
        if (result.isConfirmed) {
          this.removingId = regCourse.id;
          axios.delete('/admin/registered-courses/' + regCourse.id)
            .then(() => {
              Toast.fire({ icon: 'success', title: regCourse.code + ' removed' });
              this.loadRegCourses();
            })
            .catch(err => {
              var msg = err.response && err.response.data && err.response.data.message;
              Toast.fire({ icon: 'error', title: msg || 'Failed to remove course' });
            })
            .finally(() => { this.removingId = null; });
        }
      });
    },
    approveReg(item) {
      this.actionId = item.id;
      this.actionType = 'approve';
      axios.patch('/admin/course-registrations/' + item.id + '/approve')
        .then(res => {
          Toast.fire({ icon: 'success', title: res.data.message || 'Approved' });
          item.reg_status = '1';
        })
        .catch(err => {
          var msg = err.response && err.response.data && err.response.data.message;
          Toast.fire({ icon: 'error', title: msg || 'Failed to approve' });
        })
        .finally(() => { this.actionId = null; this.actionType = null; });
    },
    disapproveReg(item) {
      Swal.fire({
        title: 'Disapprove Registration?',
        text: 'This will allow the student to re-submit their course registration.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, disapprove',
      }).then(result => {
        if (result.isConfirmed) {
          this.actionId = item.id;
          this.actionType = 'disapprove';
          axios.patch('/admin/course-registrations/' + item.id + '/disapprove')
            .then(res => {
              Toast.fire({ icon: 'success', title: res.data.message || 'Disapproved' });
              item.reg_status = '0';
            })
            .catch(err => {
              var msg = err.response && err.response.data && err.response.data.message;
              Toast.fire({ icon: 'error', title: msg || 'Failed to disapprove' });
            })
            .finally(() => { this.actionId = null; this.actionType = null; });
        }
      });
    },
  },
};
</script>
